import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowRight, Clapperboard, Sparkles, Users } from 'lucide-react';
import styled from 'styled-components';
import { regionFromPathname } from '../utils/regionFromPath';
import { similarPath } from '../utils/paths';
import { useI18n } from '../hooks/useI18n';

const Section = styled.section`
  position: relative;
  padding: 6rem 5%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: ${({ theme }) => theme.colors.muted};
  box-sizing: border-box;
  @media (min-width: 768px) {
    padding-top: 8rem;
    padding-bottom: 8rem;
  }
  @media (min-width: 1200px) {
    padding-top: 9rem;
    padding-bottom: 9rem;
  }
`;

const AccentBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: ${({ theme }) => theme.colors.gold};
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp.hero};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.foreground};
  max-width: 56rem;
  line-height: 1.2;
  margin: 0;
  @media (min-width: 1024px) {
    font-size: clamp(2.5rem, 3vw + 1.5rem, 4rem);
  }
`;

const TitleAccent = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const Subtitle = styled.p`
  margin: 1.25rem 0 0 0;
  font-size: ${({ theme }) => theme.fontClamp.lg};
  color: ${({ theme }) => theme.colors.mutedForeground};
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FormWrap = styled(motion.form)`
  margin-top: 2.5rem;
  width: 100%;
  max-width: 52rem;
  position: relative;
  z-index: 10;
`;

const InputWrap = styled.div`
  position: relative;
`;

const SearchIconAbs = styled.span`
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: flex;
`;

const HeroInput = styled.input`
  width: 100%;
  height: 4rem;
  padding-left: 3.5rem;
  padding-right: 9rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  font-size: ${({ theme }) => theme.fontClamp.base};
  outline: none;
  border: 2px solid transparent;
  transition: all 0.2s;
  box-shadow: ${({ theme }) => theme.shadowCard};
  color: ${({ theme }) => theme.colors.foreground};
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
  @media (min-width: 1024px) {
    height: 4.5rem;
    font-size: ${({ theme }) => theme.fontClamp.lg};
  }
`;

const SubmitBtn = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  bottom: 0.5rem;
  padding: 0 2rem;
  border-radius: ${({ theme }) => theme.radius};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const ChipsWrap = styled(motion.div)`
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  z-index: 10;
`;

const Chip = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  box-shadow: ${({ theme }) => theme.shadowCard};
  transition: all 0.2s;
  border: none;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const ChipHint = styled.p`
  margin: 0.75rem 0 0;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const StepsGrid = styled(motion.div)`
  margin-top: 4rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;
  max-width: 48rem;
  z-index: 10;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  text-align: left;
`;

const StepIconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const StepLabel = styled.p`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  margin: 0;
`;

const StepDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
`;

const steps = [
  {
    icon: Clapperboard,
    label: 'Search a title',
    desc: 'Movie, TV series, or anime — same as Similaris for websites.',
    bg: 'rgba(0,48,135,0.1)',
    iconColor: 'hsl(216, 100%, 27%)',
  },
  {
    icon: Sparkles,
    label: 'See similar picks',
    desc: 'Curated and community-ranked titles that feel like your pick.',
    bg: 'rgba(220, 38, 38, 0.1)',
    iconColor: 'hsl(348, 91%, 47%)',
  },
  {
    icon: Users,
    label: 'Rate & suggest',
    desc: 'Vote how similar each match is and suggest ones we missed.',
    bg: 'rgba(128, 0, 0, 0.1)',
    iconColor: 'hsl(0, 100%, 25%)',
  },
];

const HERO_SIMILAR_CHIPS = [
  { category: 'anime_tv', tmdbId: 1429, title: 'Attack on Titan' },
  { category: 'movie', tmdbId: 27205, title: 'Inception' },
  { category: 'tv', tmdbId: 1396, title: 'Breaking Bad' },
  { category: 'anime_tv', tmdbId: 31910, title: 'Naruto Shippuden' },
];

export default function SearchHero() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const pre = region || '';
  const { t } = useI18n();

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      const base = pre ? `/${pre}` : '';
      navigate(`${base}/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const goSimilar = (item) => {
    const path = similarPath(item.category, item.tmdbId, item.title, pre);
    navigate(path);
  };

  return (
    <Section>
      <AccentBar />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.2, 0, 0, 1] }}
        style={{ position: 'relative', zIndex: 10 }}
      >
        <Badge>{t('hero.badge', 'Similar picks · Movies · TV · Anime')}</Badge>
        <Title>
          {t('hero.titlePrefix', 'Find')} <TitleAccent>{t('hero.titleAccent', 'similar')}</TitleAccent> {t('hero.titleSuffix', 'movies, TV & anime')}
        </Title>
        <Subtitle>
          {t('hero.subtitle', 'Search any title and get shows and films the community rates as similar.')}
        </Subtitle>
      </motion.div>

      <FormWrap
        onSubmit={handleSearch}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0, 0, 1] }}
      >
        <InputWrap>
          <SearchIconAbs>
            <Search size={20} strokeWidth={2.5} />
          </SearchIconAbs>
          <HeroInput
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('hero.searchPlaceholder', 'Try Attack on Titan, Inception, Stranger Things...')}
            aria-label={t('hero.searchAria', 'Search for a title to find similar picks')}
          />
          <SubmitBtn type="submit">
            {t('common.search', 'Search')} <ArrowRight size={16} strokeWidth={2.5} />
          </SubmitBtn>
        </InputWrap>
      </FormWrap>

      <ChipsWrap initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
        <ChipHint style={{ width: '100%', marginBottom: '0.25rem' }}>
          {t('hero.jumpTo', 'Jump straight to similar titles:')}
        </ChipHint>
        {HERO_SIMILAR_CHIPS.map((item) => (
          <Chip key={`${item.category}-${item.tmdbId}`} type="button" onClick={() => goSimilar(item)}>
            {item.title}
          </Chip>
        ))}
      </ChipsWrap>

      <StepsGrid
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        {steps.map((step) => (
          <StepRow key={step.label}>
            <StepIconBox $bg={step.bg} $color={step.iconColor}>
              <step.icon size={18} strokeWidth={2.5} />
            </StepIconBox>
            <div>
              <StepLabel>
                {step.label === 'Search a title'
                  ? t('hero.step1Label', 'Search a title')
                  : step.label === 'See similar picks'
                    ? t('hero.step2Label', 'See similar picks')
                    : t('hero.step3Label', 'Rate & suggest')}
              </StepLabel>
              <StepDesc>
                {step.label === 'Search a title'
                  ? t('hero.step1Desc', 'Movie, TV series, or anime.')
                  : step.label === 'See similar picks'
                    ? t('hero.step2Desc', 'Curated and community-ranked titles that feel like your pick.')
                    : t('hero.step3Desc', 'Vote how similar each match is and suggest ones we missed.')}
              </StepDesc>
            </div>
          </StepRow>
        ))}
      </StepsGrid>
    </Section>
  );
}
